import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import axios from 'src/utils/axios'
import { uniqueId } from 'lodash'
import { forceLogout } from 'src/auth/context/jwt/utils'
import keycloak from 'src/utils/keycloak'
import { showMessage } from './message'

const initialState = {
  refreshingToken: false,
  callsTail: [],
  openLoading: true,
  reInit: false,
  goTo404: false,
  goTo403: false,
}

const stopLoading = (activeLoading, thunkAPI) => {
  const { callsTail } = thunkAPI.getState().axios

  if (activeLoading && !callsTail.length) {
    return setTimeout(() => {
      thunkAPI.dispatch(updateLoading(false))
    }, 10)
  }

  if (callsTail.length) {
    return setTimeout(() => {
      stopLoading(activeLoading, thunkAPI)
    }, 1000)
  }

  return ''
}

const startLoading = (thunkAPI) => {
  thunkAPI.dispatch(updateLoading(true))
}

export const axiosSlice = createSlice({
  name: 'axios',
  initialState,
  reducers: {
    pushCallsTail: (state, action) => {
      const tmpTail = [...state.callsTail]

      tmpTail.push(action.payload)

      state.callsTail = tmpTail
    },
    removeCallsTail: (state, action) => {
      const tmpTail = [...state.callsTail]

      tmpTail.splice(state.callsTail.indexOf(action.payload), 1)

      state.callsTail = tmpTail
    },
    setRefreshingToken: (state, action) => {
      state.refreshingToken = action.payload
    },
    updateLoading: (state, action) => {
      state.openLoading = action.payload
    },
    forceReInit: (state) => {
      state.reInit = !state.reInit
    },
    redirect404: (state) => {
      state.goTo404 = !state.goTo404
    },
    redirect403: (state) => {
      state.goTo403 = !state.goTo403
    },
  },
})

export const ajaxCall = createAsyncThunk('axios/ajaxCall', async (obj, thunkAPI) => {
  const { isLoggedIn, updateToken } = keycloak

  if (isLoggedIn()) {
    updateToken()
  }

  const callId = uniqueId('call_')

  thunkAPI.dispatch(pushCallsTail(callId))

  obj.config.headers = {
    ...axios.defaults.headers.common,
    Accept: 'application/json',
  }

  obj.config.timeout = obj.config.timeout || 300000

  axios.defaults.withCredentials = false

  const { appConfig } = thunkAPI.getState().app

  const { activeLoading} = obj

  if (activeLoading) {
    startLoading(thunkAPI)
  }

  obj.config.url = appConfig.apiUrl + obj.config.url

  if (obj.config.url.includes('/:db/')) {
    obj.config.url = obj.config.url.replace('/:db/', `/todo/`)
  }

  ;(obj.config.url.match(/\/:[a-zA-Z0-9]+/is) || []).forEach((item) => {
    const param = item.replace('/:', '')

    obj.config.url = obj.config.url.replace(item, `/${obj.config.params[param]}`)
  })

  obj.config.params = {
    ...obj.config.params,
    v: new Date().getTime(),
  }

  setTimeout(() => {
    axios(obj.config)
      .then((res) => {
        if (
          res.headers.authtoken &&
          !obj.silently &&
          !obj.preventToken &&
          localStorage.getItem('token')
        ) {
          localStorage.setItem('token', res.headers.authtoken)
        }

        thunkAPI.dispatch(removeCallsTail(callId))

        stopLoading(activeLoading, thunkAPI)

        if (res.data.systemMessages) {
          Object.entries(res.data.systemMessages).forEach(([key, value]) => {
            if (!obj.silently) {
              thunkAPI.dispatch(
                showMessage({
                  ...value,
                  autoHide: false,
                })
              )
            }
          })
        }

        if (res.data.status === 'error' && res.data.message) {
          thunkAPI.dispatch(
            showMessage({
              message: res.data.message,
              type: 'error',
              autoHide: true,
            })
          )
        }

        if (res.data.forceLogout) {
          return forceLogout()
        }

        if (res.data.forceRefresh) {
          thunkAPI.dispatch(forceReInit())
        }

        return obj.success(res)
      })
      .catch((error) => {
        thunkAPI.dispatch(removeCallsTail(callId))

        if (!error) {
          return thunkAPI.dispatch(
            showMessage({
              message: 'Errore',
              type: 'error',
              autoHide: true,
            })
          )
        }

        const { data: errorData = {}, status } = error || {}

        errorData.status = status

        stopLoading(activeLoading, thunkAPI)

        switch (errorData.errorType) {
          case 'auth':
          case 'tokenExpired':
            localStorage.removeItem('token')

            break
          default:
            break
        }

        if (status === 401) {
          return setTimeout(() => {
            console.log('init app')
            // TODO store.dispatch(appActions.initApp())

            return obj.error(error)
          }, 10)
        }

        if (status === 403) {
          thunkAPI.dispatch(redirect403())
        }

        if (status === 404) {
          thunkAPI.dispatch(redirect404())
        }

        if (!obj.silently) {
          thunkAPI.dispatch(
            showMessage({
              message: errorData.message || error.message || 'Errore',
              type: 'error',
              autoHide: true,
            })
          )
        }

        if (typeof obj.error === 'function') {
          return obj.error(errorData)
        }

        return ''
      })
  }, 10)
})

export const {
  pushCallsTail,
  removeCallsTail,
  setRefreshingToken,
  updateLoading,
  forceReInit,
  redirect404,
  redirect403,
} = axiosSlice.actions

export default axiosSlice.reducer
